import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';

import { useAuth } from '../../hooks/use-auth';
import CommonAnimatedLoader from '../../components/common/animated/loader';
import PatientsFilter from '../../components/patients/filter';
import PatientsFilterProps from '../../entities/patientsFilter';
import PatientsTableComponent from '../../components/patients/table';
import { getPatients } from '../../services/api/patients';
import PatientProps from '../../entities/patient';
import { cleanParamsForUrl } from '../../services/formatter/createQueryStringObject';
import AlertPrimary from '../../components/common/alerts/alert-primary';

function PatientsListPageComponent() {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const page = query.get('page');

  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = useAuth();

  const initialState: PatientsFilterProps = useMemo(() => {
    const state: PatientsFilterProps = { treatmentStatus: undefined, page: 1, textSearch: undefined };

    searchParams.forEach((value, key) => {
      if (key === 'page') {
        state.page = parseInt(value, 10);
      } else if (key === 'startDate' || key === 'endDate') {
        state[key] = new Date(value);
      } else if (key === 'treatmentStatus' || key === 'textSearch') {
        state[key] = value;
      }
    });

    return state;
  }, [searchParams]);

  const [params, setParams] = useState<PatientsFilterProps>(initialState);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pages, setPages] = useState<number>(1);
  const [currPage, setCurrPage] = useState<number>(1);
  const [patients, setPatients] = useState<PatientProps[]>([]);
  const [error, setError] = useState<string | null>(null);

  const setFilterData = (filterData: PatientsFilterProps) => {
    setParams({ ...filterData, page: currPage });
    setSearchParams(cleanParamsForUrl(filterData));
  };

  const setPageNo = (currentPage: number) => {
    setCurrPage(currentPage);
    setParams({ ...params, page: currentPage });
    setSearchParams(cleanParamsForUrl({ ...params, page: currentPage }));
  };

  useEffect(() => {
    if (page) {
      setCurrPage(Number(page));
    }
  }, [page]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const items = await getPatients(token, params);

        setPatients(items.patients);
        setPages(items.pages);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [token, params, currPage]);

  return (
    <div>
      <header className="mb-3 mt-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold leading-tight text-gray-900">Patients</h1>
        </div>
      </header>
      <main>
        <div className="max-w-7xl mx-auto sm:px-6 lg:px-8">
          {error && (
            <div className="my-5">
              <AlertPrimary text={error} />
            </div>
          )}
          {isLoading && <CommonAnimatedLoader />}

          <PatientsFilter onUpdate={setFilterData} filterState={params} />
          {!isLoading && patients.length === 0 && <div>There is no patients</div>}
          {!!patients.length && (
            <PatientsTableComponent patients={patients} pages={pages} onUpdate={setPageNo} currPageNo={currPage} />
          )}
        </div>
      </main>
    </div>
  );
}

export default PatientsListPageComponent;
